import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Shared approve/reject confirmation dialog for approval workflows
 * (mismatch corrections + SO adjustments). Owns its own note state so the
 * parent stays simpler.
 *
 * Props:
 *   open, onClose
 *   action:      "approve" | "reject" | null   (null → dialog closed)
 *   title:       string           (e.g. "Setujui Koreksi Mismatch")
 *   description: string           (verdict-specific explanation)
 *   onConfirm:   (note: string) => Promise<void>
 *   submitting:  boolean
 *   testIdPrefix: string          (e.g. "mismatch" produces mismatch-decision-*)
 */
export default function DecisionDialog({
  open, onClose, action, title, description, onConfirm, submitting,
  testIdPrefix = "decision",
}) {
  const [note, setNote] = useState("");
  // Reset the note whenever the dialog closes so a leftover note from a
  // previous reject doesn't leak into the next approve, etc.
  React.useEffect(() => { if (!open) setNote(""); }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${testIdPrefix}-decision-dialog`} className="rounded-none">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Catatan (opsional)"
          data-testid={`${testIdPrefix}-decision-note`}
          className="rounded-none"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Batal</Button>
          <Button
            onClick={() => onConfirm(note.trim())}
            disabled={submitting}
            variant={action === "approve" ? "default" : "destructive"}
            data-testid={`${testIdPrefix}-decision-confirm`}
            className="rounded-none"
          >
            {submitting ? "Memproses…" : action === "approve" ? "Setujui" : "Tolak"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
